import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/** Monotonically-increasing counter used to discard stale fetch responses. */
let _fetchSeq = 0;
import { listDocuments } from '../../../../services';
import { parseTenantDocumentListFromResponse } from '../../../../shared/utils/apiResponse';
import { normalizeVaultDocument } from '../../utils';
import { useAgentKnowledgeEnv } from './AgentKnowledgeEnvContext.jsx';

const AgentKnowledgeDocumentsContext = createContext(null);

export function AgentKnowledgeDocumentsProvider({ children }) {
  const { vaultUuid } = useAgentKnowledgeEnv();

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  const previousVaultUuidRef = useRef(vaultUuid);
  const fetchSeqRef = useRef(0);

  const fetchDocuments = useCallback(
    async (force = false) => {
      if (!vaultUuid) return;
      if (hasFetched && !force) return;

      const seq = ++_fetchSeq;
      fetchSeqRef.current = seq;

      setLoading(true);
      setError(null);

      try {
        const response = await listDocuments({
          vault_uuid: vaultUuid,
          page_size: 100,
        });

        if (fetchSeqRef.current !== seq) return;

        const rawDocuments = parseTenantDocumentListFromResponse(response);
        const documentsList = rawDocuments.map((d) => normalizeVaultDocument(d));

        setDocuments((prev) => {
          const pendingUploads = prev.filter((d) => d.state === 'loading');
          return [...pendingUploads, ...documentsList];
        });
        setHasFetched(true);
      } catch (err) {
        if (fetchSeqRef.current === seq) setError(err);
      } finally {
        if (fetchSeqRef.current === seq) setLoading(false);
      }
    },
    [vaultUuid, hasFetched],
  );

  const addDocument = useCallback(
    (document) => {
      setDocuments((prev) => [normalizeVaultDocument(document), ...prev]);
    },
    [],
  );

  const removeDocument = useCallback((documentId) => {
    setDocuments((prev) => prev.filter((d) => (d.uuid || d.id) !== documentId));
  }, []);

  const updateDocument = useCallback((documentId, updates) => {
    setDocuments((prev) =>
      prev.map((d) => {
        const id = d.uuid || d.id;
        return id === documentId ? { ...d, ...updates } : d;
      }),
    );
  }, []);

  const refetch = useCallback(() => fetchDocuments(true), [fetchDocuments]);

  const reset = useCallback(() => {
    setDocuments([]);
    setLoading(false);
    setError(null);
    setHasFetched(false);
  }, []);

  useEffect(() => {
    const prevVault = previousVaultUuidRef.current;
    if (prevVault !== vaultUuid) {
      previousVaultUuidRef.current = vaultUuid;
      reset();
      if (vaultUuid) {
        void fetchDocuments(true);
      }
    }
  }, [vaultUuid, reset, fetchDocuments]);

  const value = useMemo(
    () => ({
      documents,
      loading,
      error,
      hasFetched,
      vaultUuid,
      fetchDocuments,
      addDocument,
      removeDocument,
      updateDocument,
      refetch,
      reset,
      documentsCount: documents.length,
    }),
    [
      documents,
      loading,
      error,
      hasFetched,
      vaultUuid,
      fetchDocuments,
      addDocument,
      removeDocument,
      updateDocument,
      refetch,
      reset,
    ],
  );

  return (
    <AgentKnowledgeDocumentsContext.Provider value={value}>
      {children}
    </AgentKnowledgeDocumentsContext.Provider>
  );
}

export function useAgentKnowledgeDocuments() {
  const ctx = useContext(AgentKnowledgeDocumentsContext);
  if (!ctx) {
    throw new Error(
      'useAgentKnowledgeDocuments must be used within AgentKnowledgeDocumentsProvider',
    );
  }
  return ctx;
}

export default AgentKnowledgeDocumentsContext;
